export interface RotaShift {
  id: string;
  userId: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  breakMinutes: number;
  timezone: string;
  normalWeeklyHours: number;
  drivingKm: number | null;
  source: 'demo' | 'import';
}
export interface RotaImportResult {
  valid: boolean;
  imported?: number;
  count?: number;
  errors: { row: number; field: string; message: string }[];
}
export interface RotaData {
  clientId: string;
  workers: { id: string; name: string }[];
  shifts: RotaShift[];
}

export function localShiftDay(s: RotaShift): string {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: s.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(s.plannedStart));
  const part = (key: string) => p.find((x) => x.type === key)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export const shiftHours = (s: RotaShift, actual = false): number | null => {
  const start = actual ? s.actualStart : s.plannedStart,
    end = actual ? s.actualEnd : s.plannedEnd;
  if (!start || !end) return null;
  return Math.max(0, (Date.parse(end) - Date.parse(start)) / 3600000 - s.breakMinutes / 60);
};
export function rotaWeek(shifts: RotaShift[], anchor = new Date().toISOString().slice(0, 10)) {
  const monday = new Date(anchor + 'T00:00:00Z');
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  const start = monday.toISOString().slice(0, 10);
  monday.setUTCDate(monday.getUTCDate() + 7);
  const end = monday.toISOString().slice(0, 10);
  const sorted = [...shifts].sort((a, b) => a.plannedStart.localeCompare(b.plannedStart));
  const week = sorted.filter((s) => {
    const day = localShiftDay(s);
    return day >= start && day < end;
  });
  const hours = week.reduce((n, s) => n + (shiftHours(s) ?? 0), 0);
  const recorded = week.filter((s) => s.actualStart && s.actualEnd);
  const actualHours = recorded.length ? recorded.reduce((n, s) => n + (shiftHours(s, true) ?? 0), 0) : null;
  const contracted = week[week.length - 1]?.normalWeeklyHours ?? 40;
  const gaps = sorted
    .slice(1)
    .flatMap((s, i) =>
      week.includes(s) ? [(Date.parse(s.plannedStart) - Date.parse(sorted[i].plannedEnd)) / 3600000] : [],
    );
  let run = 0,
    longestRun = 0,
    prev = '';
  for (const day of [...new Set(week.map(localShiftDay))].sort()) {
    run = prev && Date.parse(day) - Date.parse(prev) === 86400000 ? run + 1 : 1;
    longestRun = Math.max(longestRun, run);
    prev = day;
  }
  return {
    start,
    end,
    shifts: week,
    hours,
    actualHours,
    actualCount: recorded.length,
    contracted,
    overtime: Math.max(0, hours - contracted),
    shortestRest: gaps.length ? Math.min(...gaps) : null,
    shortRestCount: gaps.filter((g) => g < 11).length,
    longestRun,
    drivingKm: week.some((s) => s.drivingKm !== null) ? week.reduce((n, s) => n + (s.drivingKm ?? 0), 0) : null,
  };
}
